import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRegisterPageRefresh } from '@/contexts/PageRefreshContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Package,
  Clock,
  AlertTriangle,
  Truck,
  TrendingUp,
  TrendingDown,
  MoreVertical,
  Bell,
  AlertCircle,
  Calendar as CalendarIcon,
  Search,
  LogOut,
} from 'lucide-react';
import { checkInOutService } from '@/services/checkinout-service';
import type { CheckInOutDashboardStats } from '@/types/checkinout';
import { format } from 'date-fns';
import { toast } from 'sonner';

export default function CheckInOutDashboard(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<CheckInOutDashboardStats | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await checkInOutService.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Register refresh handler for breadcrumb
  useRegisterPageRefresh(loadDashboardData, location.pathname);

  const handleQuickAction = async (action: string, recordId: string) => {
    try {
      switch (action) {
        case 'remind':
          await checkInOutService.sendReminder(recordId);
          toast.success('Reminder sent successfully');
          break;
        case 'escalate':
          await checkInOutService.escalateOverdue(recordId);
          toast.success('Escalation sent to management');
          break;
        case 'schedule':
          toast.info('Collection scheduling coming soon');
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Action failed:', error);
      toast.error('Action failed');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; label: string }> = {
      'overdue': { variant: 'destructive', label: 'Overdue' },
      'due-soon': { variant: 'default', label: 'Due Soon' },
      'checked-out': { variant: 'secondary', label: 'Checked Out' },
      'in-transit': { variant: 'outline', label: 'In Transit' },
    };
    const config = variants[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const filteredOverdue = stats?.overdueList.filter(record =>
    record.assetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.assignedToName.toLowerCase().includes(searchTerm.toLowerCase())
  ) ?? [];

  if (loading) {
    return (
      <div className="flex-1 space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={`skeleton-${i}`} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Package className="h-6 w-6 text-primary" />
              Check-In / Check-Out
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Track asset movements and manage check-in/check-out operations
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/checkinout/calendar')} variant="outline">
              <CalendarIcon className="mr-2 h-4 w-4" />
              Calendar
            </Button>
            <Button onClick={() => navigate('/checkinout/checkout')} className="gap-2">
              <LogOut className="h-4 w-4" />
              Check Out Asset
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-6 p-6">

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Checked Out</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalCheckedOut ?? 0}</div>
            {stats?.checkedOutChange !== undefined && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                {stats.checkedOutChange > 0 ? (
                  <>
                    <TrendingUp className="h-3 w-3 text-green-500" />
                    <span className="text-green-500">+{stats.checkedOutChange}%</span>
                  </>
                ) : (
                  <>
                    <TrendingDown className="h-3 w-3 text-red-500" />
                    <span className="text-red-500">{stats.checkedOutChange}%</span>
                  </>
                )}
                <span>from last week</span>
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Due Today</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.dueToday ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Require attention today
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{stats?.overdue ?? 0}</div>
            {stats?.overdueChange !== undefined && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                {stats.overdueChange < 0 ? (
                  <>
                    <TrendingDown className="h-3 w-3 text-green-500" />
                    <span className="text-green-500">{stats.overdueChange}%</span>
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-3 w-3 text-red-500" />
                    <span className="text-red-500">+{stats.overdueChange}%</span>
                  </>
                )}
                <span>from last week</span>
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Transit</CardTitle>
            <Truck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.inTransit ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Currently being moved
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Overdue Items */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Overdue Items</CardTitle>
                <CardDescription>Assets that require immediate attention</CardDescription>
              </div>
              <div className="flex gap-2">
                <div className="relative w-64">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search overdue items..."
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate('/checkinout/overdue')}
                >
                  View All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredOverdue.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Package className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No overdue items</p>
                <p className="text-sm text-muted-foreground">
                  All assets are on track for return
                </p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Days Overdue</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOverdue.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{record.assetName}</div>
                            <div className="text-sm text-muted-foreground">
                              {record.assetId}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{record.assignedToName}</div>
                            {record.assignedToTeam && (
                              <div className="text-sm text-muted-foreground">
                                {record.assignedToTeam}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{record.currentLocation}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {format(new Date(record.expectedReturnDate), 'dd MMM yyyy')}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="destructive">
                            {Math.abs(record.dueInDays ?? 0)} days
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(record.status)}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleQuickAction('remind', record.id)}
                              >
                                <Bell className="mr-2 h-4 w-4" />
                                Send Reminder
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleQuickAction('escalate', record.id)}
                              >
                                <AlertCircle className="mr-2 h-4 w-4" />
                                Escalate
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleQuickAction('schedule', record.id)}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                Schedule Collection
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Demand Assets */}
        <Card>
          <CardHeader>
            <CardTitle>Top Demand Assets</CardTitle>
            <CardDescription>Most frequently checked out items</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.topDemandAssets.map((asset, index) => (
                <div key={asset.assetId} className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="font-medium">{asset.assetName}</div>
                    <div className="text-sm text-muted-foreground">
                      {asset.checkOutCount} check-outs • {asset.averageDuration} days avg
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">{asset.utilizationRate}%</div>
                    <div className="text-xs text-muted-foreground">utilisation</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Idle Assets */}
        <Card>
          <CardHeader>
            <CardTitle>Idle Assets</CardTitle>
            <CardDescription>Assets not used recently</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.idleAssets.map((asset) => (
                <div key={asset.assetId} className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                    <Package className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="font-medium">{asset.assetName}</div>
                    <div className="text-sm text-muted-foreground">
                      {asset.location}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">{asset.daysSinceLastUse} days</div>
                    <div className="text-xs text-muted-foreground">idle</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
}
